import React, { useEffect, useState } from 'react';
import { Modal, Button, Tab, Nav } from 'react-bootstrap';
import './AddActivity.css';
import TauriLoader2 from '../../TauriLoader2/TauriLoader2';
import TauriImage from '../../TauriImage/TauriImage';
import appURL from '../../../appURL.json';
import { transformActivities, transformEvents } from '../PlanningFactoryData/PlanningFactoryData';
import { getAll, getRecordById, syncDataToLocal } from '../../../localDB';
import { activityClassification, eventTypes } from '../../../visualizations/visualizations';

const AddActivity = ({ show, onHide, worksheet, selectedDate, isCreating, onAdd }) => {
    const [activities, setActivities] = useState([]);
    const [events, setEvents] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [notes, setNotes] = useState("");
    const [activeTab, setActiveTab] = useState("activity");
    const [selectedType, setSelectedType] = useState(null);

    const formattedDate = new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "long",   // Wednesday
        month: "short",    // Aug
        day: "numeric",    // 19
        year: "numeric",   // 2025
    });

    const handleProceed = () => {
        if (!onAdd) return;

        const payload = {
            Date: selectedDate ? selectedDate.toLocaleDateString("en-CA") : null,
            ReferenceID: selectedActivity?.ID,
            Description: selectedActivity?.Description,
            Remarks: notes
        };

        onAdd(payload, selectedType);
    };

    const fetchOnlineActivities = async () => {
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const usr_g = localStorage.getItem("usr_g");
            const response = await fetch(`${appURL.API_Worksheet}/activities?g=${Number(usr_g)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                console.log("Failed to fetch activity!");
                return;
            }

            const data = await response.json();

            const filteredActivities = (data.activities || []).filter(
                activity => activityClassification[activity?.classification] === activityClassification.Event
            );

            const transformAct = await transformActivities(filteredActivities);

            if (transformAct) syncDataToLocal('Activities', transformAct);
            setActivities(transformAct);
        } catch (err) {
            console.log(err);
        }
    }

    const fetchOfflineActivities = async () => {
        try {
            const act = await getAll("Activities");
            const filterAct = act.filter(a => a.ID === activityClassification.Event);
            setActivities(filterAct);
        } catch (err) {
            console.log(err);
        }
    }

    const fetchOnlineEvents = async (selectedDate) => {
        try {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const usr_g = localStorage.getItem("usr_g");
            const cycleID = worksheet?.Cycle?.ID;
            const response = await fetch(`${appURL.API_Worksheet}/events/${Number(usr_g)}/${Number(cycleID)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-Device-ID': devId
                }
            });

            if (!response.ok) {
                console.log("Failed to fetch worksheet!");
                return;
            }

            const data = await response.json();

            const transformEvent = await transformEvents(data.events);
            const formattedDate = new Date(selectedDate).toLocaleDateString('en-CA');
            const filteredEvents = transformEvent.filter(event => event.Date === formattedDate);
            if (transformEvent) syncDataToLocal('CycleEvents', transformEvent);
            setEvents(filteredEvents);
        } catch (err) {
            console.log(err);
        }
    }

    const fetchOfflineEvents = async () => {
        try {
            const e = await getAll("CycleEvents");
            const filteredEvents = e.filter(event => event.Date === formattedDate);
            setEvents(filteredEvents);
        } catch (err) {
            console.log(err);
        }
    }

    const fetchData = async (selectedDate) => {
        const isOnline = JSON.parse(localStorage.getItem("isOnline"));
        const hasInternet = navigator.onLine;

        if (isOnline && hasInternet) {
            await fetchOnlineActivities();
            await fetchOnlineEvents(selectedDate);
        } else {
            await fetchOfflineActivities();
            await fetchOfflineEvents(selectedDate);
        }
    }

    const checkEvent = async () => {
        if (worksheet?.Days && selectedDate) {
            const dateKey = new Date(selectedDate).toLocaleDateString("en-CA");
            const dayData = worksheet.Days[dateKey];

            if (dayData?.event) {
                const { type, referenceID, remarks } = dayData.event;
                if (type === eventTypes.Recurring) {
                    // Fetch from Activities
                    const activity = await getRecordById("Activities", referenceID);
                    setNotes(remarks);
                    setSelectedActivity(activity);
                    setSelectedType(eventTypes.Recurring);
                    setActiveTab("activity"); // switch to Activity tab
                } else if (type === eventTypes.Organizational) {
                    // Fetch from Events
                    const eventItem = await getRecordById("CycleEvents", referenceID);
                    setNotes(remarks);
                    setSelectedActivity(eventItem);
                    setSelectedType(eventTypes.Organizational);
                    setActiveTab("event"); // switch to Event tab
                } else {
                    setNotes("");
                    setSelectedActivity(activities[0]);
                    setSelectedType(eventTypes.Recurring);
                    setActiveTab("activity");
                }
                return true;
            } else {
                setNotes("");
                setSelectedActivity(activities[0]);
                setSelectedType(eventTypes.Recurring);
                setActiveTab("activity");
            }
        }

        return false;
    };


    useEffect(() => {
        const initialize = async () => {
            await fetchData(selectedDate);

            await checkEvent();
        };

        initialize();
    }, [show, selectedDate, worksheet]);

    return (
        <Modal
            className='add-activity-modal'
            show={show}
            onHide={onHide}
            size="lg"
            centered
            backdrop={isCreating ? "static" : true}
            keyboard={!isCreating}
        >
            <Modal.Header>
                Add Activity
            </Modal.Header>
            <Modal.Body>
                <div className="schedule-options">
                    <div className="schedule-date d-flex align-items-center">
                        <TauriImage
                            localImage={"/Images/circle-check.svg"}
                            alt="icon"
                            className={"me-2"}
                        />
                        <span>{formattedDate}</span>
                    </div>

                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="activity">Activity</Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="event">Event</Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            {/* --- Activity Tab --- */}
                            <Tab.Pane eventKey="activity">
                                <div className="activity-options">
                                    {activities.map((activity) => (
                                        <label key={activity.ID}>
                                            <input
                                                type="radio"
                                                name="activity"
                                                value={activity.ID}
                                                checked={selectedActivity?.ID === activity.ID}
                                                onChange={() => {
                                                    setSelectedActivity(activity);
                                                    setSelectedType(eventTypes.Recurring);
                                                }}
                                            />
                                            {activity.Description}
                                        </label>
                                    ))}
                                </div>
                            </Tab.Pane>

                            {/* --- Event Tab --- */}
                            <Tab.Pane eventKey="event">
                                <div className="activity-options">
                                    {events.map((event) => (
                                        <label key={event.ID}>
                                            <input
                                                type="radio"
                                                name="event"
                                                value={event.ID}
                                                checked={selectedActivity?.ID === event.ID}
                                                onChange={() => {
                                                    setSelectedActivity(event);
                                                    setSelectedType(eventTypes.Organizational);
                                                }}
                                            />
                                            {event.Description}
                                        </label>
                                    ))}
                                </div>

                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>

                    <div className='mb-3'>
                        <div className='mb-1'>Notes</div>
                        <textarea
                            id="notes"
                            className="form-control"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end">
                <Button className='btn-proceed d-flex align-items-center' disabled={isCreating} onClick={handleProceed}>
                    {isCreating && (
                        <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.2rem" color="#1D5D9B" />
                    )}
                    <span className="text-nowrap">Proceed</span>
                </Button>
                <Button className="btn-cancel d-flex" disabled={isCreating} onClick={onHide}>
                    <span className="text-nowrap">Cancel</span>
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddActivity;
